import { createElement, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import {
  Boxes,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Gauge,
  Info,
  MonitorUp,
  Search,
  Share2,
  TicketCheck,
  X
} from "lucide-react";
import type {
  StudentSimulatorAppView,
  StudentTicketWorkspaceItem,
  StudentWorkspaceSnapshot,
  TargetResultStatus,
  TicketPriority,
  TicketStatus
} from "../shared/domain/index.js";
import {
  buildScenarioSimulatorUrl,
  buildSimulatorStorageKey,
  defaultSimulatorTopology,
  legacyV1ServerOnlyAppIds,
  nodeForSimulatorId,
  resolveSimulatorBridgeAttemptId,
  simulatorTopologyForTicket,
  type SimulatorTopologyNode
} from "./simulatorBridgeClient.js";
import { NativeSimulatorDesktop } from "./simulator/NativeSimulatorDesktop.js";
import { FirstDeskSimulatorShell } from "../simulator/index.js";

type StudentRoute = { kind: "dashboard" } | { kind: "ticket"; ticketId: string } | { kind: "scenario"; ticketId: string };
type SimulatorRuntimeMode = "native" | "legacy" | "compare" | "fd-native";
const simulatorRuntimeStorageKey = "firstdesk-v2-simulator-runtime-mode";
const legacySimulatorRuntimeStorageKey = "firstdesk-v2-native-simulator-mode";

interface ReportFormState {
  summary: string;
  type: string;
  ticketStatus: string;
  analysis: string;
  actions: string;
  machines: string;
  systems: string;
  validation: string;
}

interface KnowledgeArticle {
  id: string;
  type: "actie" | "app" | "all";
  actionId: string;
  category: string;
  categoryId: string;
  appliesTo: string[];
  appIds: string[];
  appNames: string[];
  runCommands: string[];
  startPaths: string[];
  keywords: string[];
  tags: string[];
  tickets: string[];
  title: string;
  summary: string;
  steps: string[];
  searchCorpus: string;
}

type LegacyKnowledgebaseArticle = {
  id?: string;
  type?: string;
  naam?: string;
  titel?: string;
  korteBeschrijving?: string;
  bedoeldVoor?: string;
  categorie?: string;
  categorieId?: string;
  applicatie?: Array<{ id?: string; naam?: string }> | { id?: string; naam?: string };
  runCommando?: string[] | string;
  startpadNavigatie?: string[] | string;
  zoekwoordenSynoniemen?: string[] | string;
  tags?: string[] | string;
  relevanteTickets?: string[] | string;
  stapsgewijzeHandelingen?: string[] | string;
  veelvoorkomendeChecks?: string[] | string;
  valkuilenFouten?: string[] | string;
  waarTeVinden?: string[] | string;
};

type LegacyKnowledgebaseApp = {
  id?: string;
  naam?: string;
  categorie?: string;
  bedoeldVoor?: string;
  tags?: string[] | string;
  waarTeVinden?: {
    runCommands?: string[] | string;
    startpad?: string[] | string;
  };
};

type LegacyKnowledgebaseAction = {
  id?: string;
  titel?: string;
  categorie?: string;
  bedoeldVoor?: string;
  tags?: string[] | string;
  relevanteTickets?: string[] | string;
  waarTeVinden?: {
    primaireRoute?: string;
    gerelateerdeApps?: string[] | string;
  };
};

type LegacyKnowledgebasePayload = {
  artikelen?: LegacyKnowledgebaseArticle[];
  apps?: LegacyKnowledgebaseApp[];
  actions?: LegacyKnowledgebaseAction[];
  taxonomie?: {
    filters?: {
      categorieen?: Array<{ id?: string; naam?: string }>;
    };
  };
};

function asKnowledgeSteps(...values: Array<string[] | string | undefined>) {
  const steps = values.flatMap((value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }).map((step) => String(step).trim()).filter(Boolean);
  return steps.length ? steps.slice(0, 8) : ["Lees de legacy kennisbankinstructie en voer de stappen in de simulator uit."];
}

function normalizeKnowledgeToken(value: unknown) {
  return String(value ?? "").toLowerCase().trim();
}

function normalizeKnowledgeType(type: unknown): KnowledgeArticle["type"] {
  const token = normalizeKnowledgeToken(type);
  if (token === "actions" || token === "actie") return "actie";
  if (token === "apps" || token === "app") return "app";
  return "all";
}

function asKnowledgeArray(value: string[] | string | undefined) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return Array.isArray(value) ? value.map((item) => String(item ?? "").trim()).filter(Boolean) : [];
}

function firstKnowledgeText(...values: unknown[]) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function knowledgeSearchCorpus(parts: unknown[]) {
  return parts.flatMap((part) => Array.isArray(part) ? part : [part])
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function legacyKnowledgeArticleToView(article: LegacyKnowledgebaseArticle, index: number, appNameById = new Map<string, string>()): KnowledgeArticle {
  const type = normalizeKnowledgeType(article.type);
  const appRefs = Array.isArray(article.applicatie)
    ? article.applicatie
    : article.applicatie
      ? [article.applicatie]
      : [];
  const appIds = appRefs.map((app) => normalizeKnowledgeToken(app.id)).filter(Boolean);
  const appNames = appRefs.map((app) => firstKnowledgeText(app.naam, app.id)).filter(Boolean);
  const actionId = type === "actie" ? normalizeKnowledgeToken(String(article.id ?? "").replace(/^actie:/, "")) : "";
  const runCommands = asKnowledgeArray(article.runCommando);
  const startPaths = asKnowledgeArray(article.startpadNavigatie);
  const keywords = asKnowledgeArray(article.zoekwoordenSynoniemen);
  const tags = asKnowledgeArray(article.tags);
  const tickets = asKnowledgeArray(article.relevanteTickets);
  const category = firstKnowledgeText(article.categorie, article.categorieId, "Algemeen");
  const categoryId = normalizeKnowledgeToken(article.categorieId || article.categorie || "algemeen");
  const title = firstKnowledgeText(article.titel, article.naam, article.id, `Kennisbankartikel ${index + 1}`);
  const summary = firstKnowledgeText(article.korteBeschrijving, article.bedoeldVoor, "Legacy FirstDesk kennisbankartikel.");
  const resolvedAppNames = appNames.length
    ? appNames
    : appIds.map((appId) => appNameById.get(appId) ?? appId);
  return {
    id: article.id ?? `legacy-kb-${index}`,
    type,
    actionId,
    appliesTo: resolvedAppNames.length ? resolvedAppNames : ["Algemeen"],
    appIds,
    appNames: resolvedAppNames,
    category,
    categoryId,
    keywords,
    runCommands,
    searchCorpus: knowledgeSearchCorpus([
      title,
      summary,
      category,
      categoryId,
      actionId,
      runCommands,
      startPaths,
      keywords,
      tags,
      appIds,
      resolvedAppNames,
      tickets
    ]),
    startPaths,
    steps: asKnowledgeSteps(article.stapsgewijzeHandelingen, article.veelvoorkomendeChecks, article.valkuilenFouten, article.waarTeVinden),
    summary,
    tags,
    tickets,
    title
  };
}

function buildKnowledgeItems(payload: LegacyKnowledgebasePayload): KnowledgeArticle[] {
  const appNameById = new Map((payload.apps ?? []).map((app) => [
    normalizeKnowledgeToken(app.id),
    firstKnowledgeText(app.naam, app.id)
  ]));

  const articles = (payload.artikelen ?? []).map((article, index) => legacyKnowledgeArticleToView(article, index, appNameById));
  if (articles.length) return articles;

  const appItems = (payload.apps ?? []).map((app, index): KnowledgeArticle => {
    const id = normalizeKnowledgeToken(app.id) || `app-${index}`;
    const runCommands = asKnowledgeArray(app.waarTeVinden?.runCommands);
    const startPaths = asKnowledgeArray(app.waarTeVinden?.startpad);
    const tags = asKnowledgeArray(app.tags);
    const title = firstKnowledgeText(app.naam, app.id, `App ${index + 1}`);
    const summary = firstKnowledgeText(app.bedoeldVoor, "Praktische beheerinterface in de simulator.");
    const category = firstKnowledgeText(app.categorie, "Algemeen");
    const categoryId = normalizeKnowledgeToken(category);
    return {
      id: `app:${id}`,
      type: "app",
      actionId: "",
      appliesTo: [title],
      appIds: [id],
      appNames: [title],
      category,
      categoryId,
      keywords: tags,
      runCommands,
      searchCorpus: knowledgeSearchCorpus([title, summary, category, runCommands, startPaths, tags, id]),
      startPaths,
      steps: asKnowledgeSteps(startPaths, runCommands, summary),
      summary,
      tags,
      tickets: [],
      title
    };
  });

  const actionItems = (payload.actions ?? []).map((action, index): KnowledgeArticle => {
    const id = normalizeKnowledgeToken(action.id) || `actie-${index}`;
    const appIds = asKnowledgeArray(action.waarTeVinden?.gerelateerdeApps).map(normalizeKnowledgeToken);
    const appNames = appIds.map((appId) => appNameById.get(appId) ?? appId);
    const route = firstKnowledgeText(action.waarTeVinden?.primaireRoute);
    const tags = asKnowledgeArray(action.tags);
    const tickets = asKnowledgeArray(action.relevanteTickets);
    const title = firstKnowledgeText(action.titel, action.id, `Actie ${index + 1}`);
    const summary = firstKnowledgeText(action.bedoeldVoor, "Praktische handeling om tickettarget te halen.");
    const category = firstKnowledgeText(action.categorie, "Algemeen");
    const categoryId = normalizeKnowledgeToken(category);
    return {
      id: `actie:${id}`,
      type: "actie",
      actionId: id,
      appliesTo: appNames.length ? appNames : ["Algemeen"],
      appIds,
      appNames,
      category,
      categoryId,
      keywords: tags,
      runCommands: [],
      searchCorpus: knowledgeSearchCorpus([title, summary, category, id, route, tags, tickets, appIds, appNames]),
      startPaths: route ? [route] : [],
      steps: asKnowledgeSteps(route, summary),
      summary,
      tags,
      tickets,
      title
    };
  });

  return [...appItems, ...actionItems];
}

function resolveSimulatorRuntimeMode(): SimulatorRuntimeMode {
  if (typeof window === "undefined") return "fd-native";

  const params = new URLSearchParams(window.location.search);
  const legacyStoredMode = window.localStorage.getItem(legacySimulatorRuntimeStorageKey);
  const storedMode = window.localStorage.getItem(simulatorRuntimeStorageKey)
    ?? (legacyStoredMode && legacyStoredMode !== "native" ? legacyStoredMode : null);
  const requested = params.get("nativeSimulatorMode")
    ?? params.get("simulatorMode")
    ?? storedMode
    ?? "fd-native";

  return requested === "native" || requested === "compare" || requested === "fd-native" ? requested : "legacy";
}

function statusLabel(status: TicketStatus) {
  if (status === "in_progress") return "In behandeling";
  if (status === "review_required") return "Wacht op review";
  if (status === "closed") return "Afgesloten";
  return "Open";
}

function priorityLabel(priority: TicketPriority) {
  if (priority === "hoog") return "Hoog";
  if (priority === "gemiddeld") return "Gemiddeld";
  return "Laag";
}

function priorityClass(priority: TicketPriority) {
  return `priority priority-${priority}`;
}

function targetStatusLabel(status: TargetResultStatus) {
  if (status === "passed") return "Gereed";
  if (status === "failed") return "Aandacht nodig";
  if (status === "manual_review") return "Docentreview";
  if (status === "escalated") return "Geescaleerd";
  return "Open";
}

function targetStatusClass(status: TargetResultStatus) {
  if (status === "passed") return "is-passed";
  if (status === "failed") return "is-failed";
  if (status === "manual_review") return "is-manual-review";
  if (status === "escalated") return "is-escalated";
  return "is-open";
}

function targetStatusHint(status: TargetResultStatus, hidden: boolean) {
  if (status === "passed") {
    return hidden ? "Vrijgegeven na behalen" : "Automatisch bevestigd";
  }

  if (status === "failed") {
    return "Controle nodig voordat deze stap slaagt";
  }

  if (status === "manual_review") {
    return "Wacht op docentcontrole";
  }

  if (status === "escalated") {
    return "Geescaleerd naar docent";
  }

  return hidden ? "Vrijgegeven na behalen" : "Nog open";
}

function completionPercent(completed: number, total: number) {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

function emptyReportForm(ticket: StudentTicketWorkspaceItem): ReportFormState {
  const topology = simulatorTopologyForTicket({ environment: ticket.environment });
  return {
    summary: `Ticket #${ticket.number} - ${ticket.title}`,
    type: ticket.type,
    ticketStatus: "opgelost",
    analysis: "",
    actions: "",
    machines: topology.nodes.map((node) => node.id).join(", "),
    systems: ticket.environment,
    validation: ""
  };
}

function KpiGrid({
  activeFilter,
  onFilter,
  tickets,
  simulatorApps
}: {
  activeFilter: string;
  onFilter: (filter: "open" | "closed" | "review" | "all") => void;
  tickets: StudentTicketWorkspaceItem[];
  simulatorApps: StudentSimulatorAppView[];
}) {
  const totalTargets = tickets.reduce((sum, ticket) => sum + ticket.targetSummary.totalTargets, 0);
  const completedTargets = tickets.reduce((sum, ticket) => sum + ticket.targetSummary.completedTargets, 0);
  const openTickets = tickets.filter((ticket) => ticket.status !== "closed").length;
  const hiddenTargets = tickets.reduce((sum, ticket) => sum + ticket.targetSummary.hiddenRemainingCount, 0);

  const items = [
    { label: "Open tickets", value: String(openTickets), icon: TicketCheck, filter: "open" as const },
    { label: "Targets gereed", value: `${completedTargets}/${totalTargets}`, icon: ClipboardCheck, filter: "all" as const },
    { label: "Verborgen targets", value: String(hiddenTargets), icon: Gauge, filter: "review" as const },
    { label: "Simulator apps", value: String(simulatorApps.length), icon: Boxes, filter: "all" as const }
  ];

  return (
    <div className="fd-work-kpi-grid" aria-label="Kerncijfers">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            className={`fd-work-kpi-card ${activeFilter === item.filter ? "is-active" : ""}`}
            data-kpi-filter={item.filter}
            key={item.label}
            type="button"
            onClick={() => onFilter(item.filter)}
          >
            <p className="fd-work-kpi-label">{item.label}</p>
            <p className="fd-work-kpi-value">{item.value}</p>
            <span className="fd-bento-icon" aria-hidden="true">
              <Icon size={22} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TargetList({ ticket }: { ticket: StudentTicketWorkspaceItem }) {
  const completedPct = completionPercent(ticket.targetSummary.completedTargets, ticket.targetSummary.totalTargets);
  return (
    <section className="panel fd-v2-target-panel" aria-labelledby="targets-title">
      <header className="panel-head">
        <h2 id="targets-title">
          <ClipboardCheck aria-hidden="true" size={19} />
          Targets
        </h2>
        {ticket.targetSummary.hiddenRemainingCount > 0 ? (
          <span className="hidden-count">{ticket.targetSummary.hiddenRemainingCount} verborgen</span>
        ) : (
          <span className="hidden-count hidden-count-done">Geen verborgen targets</span>
        )}
      </header>
      <p className="fd-table-sub">{ticket.targetSummary.completedTargets}/{ticket.targetSummary.totalTargets} gereed ({completedPct}%)</p>

      <ul className="target-list">
        {ticket.targetSummary.visibleTargets.map((target) => (
          <li className="target-item" key={target.id}>
            <span className={`target-check ${targetStatusClass(target.status)}`}>
              {target.status === "passed" ? <CheckCircle2 aria-hidden="true" size={16} /> : <Clock3 aria-hidden="true" size={16} />}
            </span>
            <span className="target-copy">
              <strong>{target.title}</strong>
              <small>{target.description || targetStatusHint(target.status, target.revealedByCompletion)}</small>
              {target.revealedByCompletion ? <small>Vrijgegeven na afronding van attempt</small> : null}
              <em className={`target-pill ${targetStatusClass(target.status)}`}>{targetStatusLabel(target.status)}</em>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StudentDashboard({
  onOpenTicket,
  onRefresh,
  snapshot
}: {
  onOpenTicket: (ticketId: string) => void;
  onRefresh?: () => void;
  snapshot: StudentWorkspaceSnapshot;
}) {
  const tickets = snapshot.tickets;
  const badge = snapshot.badge ?? {
    active: null,
    completedTickets: tickets.filter((ticket) => ticket.status === "closed").length,
    levels: [],
    next: null
  };
  const profile = snapshot.profile ?? {
    name: "Student",
    email: "-",
    role: "Student",
    institutionName: "-",
    createdAt: "-",
    lastLoginAt: "-"
  };
  const highPriority = tickets.filter((ticket) => ticket.priority === "hoog").length;
  const nextTicket = tickets.find((ticket) => ticket.status !== "closed");
  const closedTickets = tickets.filter((ticket) => ticket.status === "closed");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"priority" | "status" | "number">("priority");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [ticketPage, setTicketPage] = useState(1);
  const [liveNow, setLiveNow] = useState(() => new Date());
  const [joinCode, setJoinCode] = useState("");
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinMessage, setJoinMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [badgeShareBusy, setBadgeShareBusy] = useState(false);
  const [badgeShare, setBadgeShare] = useState<{ url: string; expiresAt: string | null; linkedInShareUrl?: string } | null>(null);
  const [badgeShareMessage, setBadgeShareMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const filteredTickets = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const priorityRank: Record<TicketPriority, number> = { laag: 1, gemiddeld: 2, hoog: 3 };

    return tickets.filter((ticket) => {
      const haystack = [
        ticket.number,
        ticket.title,
        ticket.reporter,
        ticket.environment,
        ticket.type,
        ticket.dueLabel
      ].join(" ").toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
      const matchesType = typeFilter === "all" || ticket.type.toLowerCase() === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    }).sort((left, right) => {
      const direction = sortDir === "asc" ? 1 : -1;
      if (sortBy === "priority") return ((priorityRank[left.priority] ?? 0) - (priorityRank[right.priority] ?? 0)) * direction;
      if (sortBy === "status") return statusLabel(left.status).localeCompare(statusLabel(right.status), "nl-NL") * direction;
      return left.number.localeCompare(right.number, "nl-NL", { numeric: true }) * direction;
    });
  }, [searchTerm, sortBy, sortDir, statusFilter, tickets, typeFilter]);
  const ticketPageSize = 8;
  const ticketPageCount = Math.max(1, Math.ceil(filteredTickets.length / ticketPageSize));
  const pagedTickets = filteredTickets.slice((ticketPage - 1) * ticketPageSize, ticketPage * ticketPageSize);
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setSortBy("priority");
    setSortDir("desc");
    setTicketPage(1);
  };
  const applyKpiFilter = (filter: "open" | "closed" | "review" | "all") => {
    setStatusFilter(filter === "open" ? "open" : filter === "closed" ? "closed" : filter === "review" ? "review_required" : "all");
    setTicketPage(1);
  };
  const handleGroupJoin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedJoinCode = joinCode.trim().toUpperCase();
    if (!normalizedJoinCode) {
      setJoinMessage({ type: "error", text: "Vul een groepscode in." });
      return;
    }

    setJoinBusy(true);
    setJoinMessage(null);
    try {
      const response = await fetch("/api/v2/student/groups/join", {
        body: JSON.stringify({ joinCode: normalizedJoinCode }),
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        method: "POST"
      });
      const payload = await response.json().catch(() => null) as { status?: string; group?: { className?: string }; error?: string } | null;

      if (!response.ok || !payload) {
        const message = payload?.error === "group_join_code_not_found"
          ? "Deze groepscode is niet gevonden."
          : "Aanmelden bij de groep is niet gelukt.";
        setJoinMessage({ type: "error", text: message });
        return;
      }

      setJoinMessage({
        type: "success",
        text: payload.status === "already_joined"
          ? `Je was al aangemeld bij ${payload.group?.className ?? "deze groep"}.`
          : `Je bent aangemeld bij ${payload.group?.className ?? "de groep"}.`
      });
      setJoinCode("");
      onRefresh?.();
    } catch {
      setJoinMessage({ type: "error", text: "Aanmelden bij de groep is tijdelijk niet beschikbaar." });
    } finally {
      setJoinBusy(false);
    }
  };

  const handleCreateBadgeShareLink = async () => {
    if (badgeShareBusy || !badge.active) return;
    setBadgeShareBusy(true);
    setBadgeShareMessage(null);
    try {
      const response = await fetch("/api/v2/student/badge/share-link", {
        credentials: "include"
      });
      const payload = await response.json().catch(() => null) as {
        url?: string;
        expiresAt?: string | null;
        linkedInShareUrl?: string;
        error?: string;
      } | null;
      if (!response.ok || !payload?.url) {
        setBadgeShareMessage({
          type: "error",
          text: payload?.error === "badge_not_unlocked"
            ? "Je hebt nog geen badge vrijgespeeld."
            : "Badgelink maken is mislukt."
        });
        return;
      }
      setBadgeShare({
        url: payload.url,
        expiresAt: payload.expiresAt ?? null,
        linkedInShareUrl: payload.linkedInShareUrl
      });
      try {
        await navigator.clipboard?.writeText(payload.url);
        setBadgeShareMessage({ type: "success", text: "Badgelink gemaakt en gekopieerd. De link blijft 15 dagen geldig." });
      } catch {
        setBadgeShareMessage({ type: "success", text: "Badgelink gemaakt. De link blijft 15 dagen geldig." });
      }
    } catch {
      setBadgeShareMessage({ type: "error", text: "Badgelink maken is tijdelijk niet beschikbaar." });
    } finally {
      setBadgeShareBusy(false);
    }
  };

  useEffect(() => {
    function handleGlobalSearch(event: Event) {
      const nextValue = event instanceof CustomEvent && typeof event.detail === "string" ? event.detail : "";
      setSearchTerm(nextValue);
      setTicketPage(1);
    }

    window.addEventListener("firstdesk:student-search", handleGlobalSearch);
    return () => window.removeEventListener("firstdesk:student-search", handleGlobalSearch);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setLiveNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setTicketPage(1);
  }, [searchTerm, sortBy, sortDir, statusFilter, typeFilter]);

  useEffect(() => {
    setTicketPage((page) => Math.min(page, ticketPageCount));
  }, [ticketPageCount]);

  return (
    <>
      <header className="fd-app-page-header fd-work-header">
        <div className="fd-app-page-header-left">
          <nav className="fd-breadcrumb" aria-label="Breadcrumb">
            <span>FirstDesk</span>
            <span className="fd-breadcrumb-dot" />
            <span className="fd-breadcrumb-active">Servicedesk Werkvoorraad</span>
          </nav>
          <h1>Servicedesk Werkvoorraad</h1>
          <p>Open je werkplek vanuit een ticket. Elke simulatoromgeving hoort bij een concrete melding.</p>
        </div>
        <div className="fd-student-live-clock" aria-label="Live tijd">
          <Clock3 aria-hidden="true" size={18} />
          <span>{liveNow.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>
          <small>{liveNow.toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "short" })}</small>
        </div>
      </header>

      <KpiGrid activeFilter={statusFilter === "open" ? "open" : statusFilter === "closed" ? "closed" : statusFilter === "review_required" ? "review" : "all"} onFilter={applyKpiFilter} tickets={tickets} simulatorApps={snapshot.simulatorApps} />

      <section className="fd-workqueue-card" id="tickets">
        <div className="fd-workqueue-head">
          <div>
            <h2 className="fd-panel-title">Werkvoorraad</h2>
            <p>Alle actieve tickets die aan jou zijn toegewezen.</p>
          </div>
          <span className="fd-workqueue-count">{tickets.length} tickets</span>
        </div>

        <div className="fd-workqueue-toolbar" aria-label="Werkvoorraad filters">
          <label className="fd-workqueue-search">
            <Search aria-hidden="true" size={16} />
            <input
              id="fd-ticket-search"
              type="search"
              placeholder="Zoek op ticket, melder of systeem"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as TicketStatus | "all")}
          >
            <option value="all">Alle statussen</option>
            <option value="open">Open</option>
            <option value="in_progress">In behandeling</option>
            <option value="review_required">Review nodig</option>
            <option value="closed">Afgesloten</option>
          </select>
          <select
            aria-label="Type filter"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
          >
            <option value="all">Alle types</option>
            <option value="servicedesk">Servicedesk</option>
            <option value="game">Game</option>
          </select>
          <select
            aria-label="Sorteer werkvoorraad"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value as "priority" | "status" | "number")}
          >
            <option value="priority">Prioriteit</option>
            <option value="status">Status</option>
            <option value="number">Ticketnummer</option>
          </select>
          <button type="button" onClick={() => setSortDir((current) => current === "asc" ? "desc" : "asc")}>
            {sortDir === "asc" ? "Oplopend" : "Aflopend"}
          </button>
          <button type="button" onClick={resetFilters}>Reset</button>
        </div>

        <div className="fd-workqueue-table-wrap" aria-live="polite">
          <table className="fd-workqueue-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Type</th>
                <th>Prioriteit</th>
                <th>Status</th>
                <th>Toegewezen</th>
                <th>Melder</th>
                <th>Systeem</th>
                <th>Tijd open</th>
                <th>SLA</th>
              </tr>
            </thead>
            <tbody>
              {pagedTickets.map((ticket) => (
                <tr className="fd-workqueue-row-clickable" key={ticket.id} onClick={() => onOpenTicket(ticket.id)}>
                  <td>
                    <button className="fd-ticket-link" type="button" onClick={(event) => {
                      event.stopPropagation();
                      onOpenTicket(ticket.id);
                    }}>
                      <strong>#{ticket.number}</strong>
                      <span>{ticket.title}</span>
                    </button>
                  </td>
                  <td><span className="fd-work-badge">{ticket.type.toUpperCase()}</span></td>
                  <td><span className={priorityClass(ticket.priority)}>{priorityLabel(ticket.priority)}</span></td>
                  <td>
                    <span className={`status-badge status-${ticket.status}`}>
                      <span className="status-dot" />
                      {statusLabel(ticket.status)}
                    </span>
                  </td>
                  <td>Jij</td>
                  <td>{ticket.reporter}</td>
                  <td>{ticket.environment}</td>
                  <td>{ticket.dueLabel}</td>
                  <td><span className="fd-work-badge fd-work-badge--sla">Geen SLA</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredTickets.length && (
            <div className="fd-student-ticket-empty">
              Geen tickets gevonden met deze filters.
            </div>
          )}
          <div className="fd-v2-pagination">
            <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => setTicketPage((page) => Math.max(1, page - 1))} disabled={ticketPage <= 1}>Vorige</button>
            <span>Pagina {ticketPage} van {ticketPageCount} - {filteredTickets.length} tickets</span>
            <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => setTicketPage((page) => Math.min(ticketPageCount, page + 1))} disabled={ticketPage >= ticketPageCount}>Volgende</button>
          </div>
        </div>
      </section>

      <div className="fd-work-secondary-grid">
        <section className="fd-bento-stat fd-bento-stat--white" id="student-group">
          <h2 className="fd-panel-title">Groep aanmelden</h2>
          <form className="fd-student-join-form" onSubmit={handleGroupJoin}>
            <label htmlFor="student-group-code">Groepscode</label>
            <div>
              <input
                id="student-group-code"
                inputMode="text"
                maxLength={12}
                minLength={4}
                placeholder="AB12CD"
                value={joinCode}
                onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
              />
              <button type="submit" disabled={joinBusy}>{joinBusy ? "Bezig..." : "Aanmelden"}</button>
            </div>
            {joinMessage ? <p className={`fd-student-join-message ${joinMessage.type}`}>{joinMessage.text}</p> : null}
          </form>
          <div className="fd-work-panel-list">
            {snapshot.classes.length > 0 ? snapshot.classes.map((classGroup) => (
              <div className="fd-work-panel-item" key={classGroup.classGroupId}>
                <span>{classGroup.classCode}</span>
                <strong>{classGroup.className}</strong>
              </div>
            )) : (
              <div className="fd-work-panel-item"><span>Mijn groepen</span><strong>Geen groep gekoppeld</strong></div>
            )}
          </div>
        </section>
        <section className="fd-bento-stat fd-bento-stat--white" id="student-profile">
          <h2 className="fd-panel-title">Mijn profiel</h2>
          <div className="fd-profile-account-list fd-profile-account-list--v2">
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">Naam</span>
              <span className="fd-profile-value">{profile.name}</span>
            </div>
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">E-mail</span>
              <span className="fd-profile-value">{profile.email}</span>
            </div>
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">Rol</span>
              <span className="fd-profile-value">{profile.role}</span>
            </div>
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">Instelling</span>
              <span className="fd-profile-value">{profile.institutionName}</span>
            </div>
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">Registratiedatum</span>
              <span className="fd-profile-value">{profile.createdAt}</span>
            </div>
            <div className="fd-profile-account-row">
              <span className="fd-profile-label">Laatste login</span>
              <span className="fd-profile-value">{profile.lastLoginAt}</span>
            </div>
          </div>
          <div className="fd-student-level-card">
            <span>Servicedesk-level</span>
            <strong>{badge.active?.title ?? "Eerstelijns Medewerker"}</strong>
            <small>{badge.next ? `Nog ${badge.next.remaining} succesvolle tickets tot ${badge.next.title}.` : "Alle badgelevels zijn vrijgespeeld."}</small>
          </div>
          <div className="fd-badge-share-box">
            {badge.active ? (
              <>
                <button className="fd-table-action" type="button" onClick={() => void handleCreateBadgeShareLink()} disabled={badgeShareBusy}>
                  <Share2 aria-hidden="true" size={15} />
                  {badgeShareBusy ? "Link maken..." : "Maak badgelink"}
                </button>
                {badgeShare ? (
                  <div className="fd-badge-share-result">
                    <a href={badgeShare.url} rel="noreferrer" target="_blank">{badgeShare.url}</a>
                    <small>Geldig tot {badgeShare.expiresAt ? new Date(badgeShare.expiresAt).toLocaleString("nl-NL") : "onbekend"}</small>
                    {badgeShare.linkedInShareUrl ? <a href={badgeShare.linkedInShareUrl} rel="noreferrer" target="_blank">Deel op LinkedIn</a> : null}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="fd-badge-share-empty">Nog {badge.next?.remaining ?? 10} succesvolle tickets tot je eerste deelbare badge.</p>
            )}
            {badgeShareMessage ? <p className={`fd-student-join-message ${badgeShareMessage.type}`}>{badgeShareMessage.text}</p> : null}
          </div>
        </section>
        <section className="fd-bento-stat fd-bento-stat--white" id="student-results">
          <h2 className="fd-panel-title">Badge-overzicht</h2>
          <div className="fd-work-panel-list">
            {badge.levels.length > 0 ? badge.levels.map((level) => (
              <div className="fd-work-panel-item" key={level.key}>
                <span>{level.title}</span>
                <strong>{level.unlocked ? "Behaald" : `${level.remaining} tickets`}</strong>
              </div>
            )) : (
              <div className="fd-work-panel-item"><span>Badges</span><strong>Nog geen data</strong></div>
            )}
          </div>
        </section>
        <section className="fd-bento-stat fd-bento-stat--white" id="closed-tickets">
          <h2 className="fd-panel-title">Afgesloten tickets</h2>
          <p className="fd-muted-copy">V1-parity lijst met de tickets die meetellen voor je servicedesk-level.</p>
          <div className="fd-closed-ticket-list">
            {closedTickets.length > 0 ? closedTickets.map((ticket) => (
              <button className="fd-closed-ticket-row" type="button" key={ticket.id} onClick={() => onOpenTicket(ticket.id)}>
                <span>
                  <strong>#{ticket.number} - {ticket.title}</strong>
                  <small>{ticket.environment} · {ticket.reporter}</small>
                </span>
                <em>Afgesloten</em>
              </button>
            )) : (
              <div className="fd-student-ticket-empty">Nog geen afgesloten tickets.</div>
            )}
          </div>
          <div className="fd-work-panel-list fd-work-panel-list--compact">
            <div className="fd-work-panel-item"><span>Hoge prioriteit</span><strong>{highPriority}</strong></div>
            <div className="fd-work-panel-item"><span>Eerstvolgende focus</span><strong>{nextTicket ? `#${nextTicket.number}` : "Geen"}</strong></div>
            <div className="fd-work-panel-item"><span>Open tickets</span><strong>{tickets.filter((ticket) => ticket.status !== "closed").length}</strong></div>
          </div>
        </section>
      </div>
    </>
  );
}

function TicketDetailView({
  onBack,
  onOpenScenario,
  ticket
}: {
  onBack: () => void;
  onOpenScenario: () => void;
  ticket: StudentTicketWorkspaceItem;
}) {
  const visibleResults = ticket.targetSummary.visibleTargets.filter((target) => target.status !== "not_started");
  const passedResults = visibleResults.filter((target) => target.status === "passed").length;
  const manualReviewResults = visibleResults.filter((target) => target.status === "manual_review").length;
  const failedResults = visibleResults.filter((target) => target.status === "failed").length;
  const feedbackAvailable = visibleResults.length > 0;
  const revealedTargets = ticket.targetSummary.visibleTargets.filter((target) => target.revealedByCompletion);
  const completionPct = completionPercent(ticket.targetSummary.completedTargets, ticket.targetSummary.totalTargets);

  return (
    <article className="fd-ticket-page">
      <header className="fd-ticket-header">
        <div>
          <nav className="fd-breadcrumb" aria-label="Breadcrumb">
            <button type="button" onClick={onBack}>Dashboard</button>
            <span className="fd-breadcrumb-dot" />
            <span className="fd-breadcrumb-active">Ticket #{ticket.number}</span>
          </nav>
          <h1>{ticket.title}</h1>
          <div className="fd-ticket-badges">
            <span className="fd-work-badge">{ticket.type}</span>
            <span className={`status-badge status-${ticket.status}`}><span className="status-dot" />{statusLabel(ticket.status)}</span>
            <span className={priorityClass(ticket.priority)}>{priorityLabel(ticket.priority)}</span>
          </div>
        </div>
        <button className="fd-app-btn fd-app-btn--gradient" type="button" onClick={onOpenScenario}>
          <MonitorUp aria-hidden="true" size={18} />
          Open werkplek
        </button>
      </header>

      <div className="fd-ticket-grid">
        <div className="fd-ticket-column">
          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Melding</h2>
            <p>{ticket.description}</p>
          </section>

          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Werkacties</h2>
            <div className="fd-ticket-action-list">
              <button className="fd-app-btn fd-app-btn--gradient" type="button" onClick={onOpenScenario}>Open scenario</button>
              <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={onBack}>Terug naar werkvoorraad</button>
            </div>
          </section>

          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Docentfeedback</h2>
            {feedbackAvailable ? (
              <div className="fd-ticket-feedback">
                <p>Beoordeling beschikbaar voor {visibleResults.length} zichtbare targets.</p>
                <p className="fd-table-sub">Voortgang: {ticket.targetSummary.completedTargets}/{ticket.targetSummary.totalTargets} ({completionPct}%)</p>
                <div className="fd-ticket-feedback-pills">
                  <span className="target-pill is-passed">{passedResults} gereed</span>
                  {manualReviewResults > 0 ? <span className="target-pill is-manual-review">{manualReviewResults} in review</span> : null}
                  {failedResults > 0 ? <span className="target-pill is-failed">{failedResults} aandachtspunten</span> : null}
                </div>
              </div>
            ) : (
              <p>Feedback verschijnt zodra je verslag is beoordeeld.</p>
            )}
          </section>

          {revealedTargets.length > 0 ? (
            <section className="fd-ticket-card">
              <h2 className="fd-panel-title">Verborgen doelen na afronding</h2>
              <p className="fd-table-sub">Deze doelen waren verborgen tijdens uitvoering en zijn nu zichtbaar na beoordeling.</p>
              <ul className="fd-ticket-reveal-list">
                {revealedTargets.map((target) => (
                  <li key={target.id}>{target.title}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <div className="fd-ticket-column">
          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Ticketmetadata</h2>
            <dl className="meta-grid">
              <div><dt>Type</dt><dd>{ticket.type}</dd></div>
              <div><dt>Status</dt><dd>{statusLabel(ticket.status)}</dd></div>
              <div><dt>Topic</dt><dd>{ticket.topic}</dd></div>
              <div><dt>Complexiteit</dt><dd>{ticket.complexity}</dd></div>
              <div><dt>Omgeving</dt><dd>{ticket.environment}</dd></div>
              <div><dt>Deadline</dt><dd>{ticket.dueLabel}</dd></div>
              <div><dt>Melder</dt><dd>{ticket.reporter}</dd></div>
              <div><dt>Docent</dt><dd>{ticket.teacher}</dd></div>
              <div><dt>Toegewezen aan</dt><dd>Jij</dd></div>
              <div><dt>Targets gereed</dt><dd>{ticket.targetSummary.completedTargets}/{ticket.targetSummary.totalTargets}</dd></div>
            </dl>
          </section>

          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Werkstappen</h2>
            <TargetList ticket={ticket} />
          </section>

          <section className="fd-ticket-card">
            <h2 className="fd-panel-title">Didactische ondersteuning</h2>
            <p>Lees de melding, onderzoek de omgeving en onderbouw je oplossing in je servicedeskverslag.</p>
          </section>
        </div>
      </div>
    </article>
  );
}

function LegacyV1SimulatorHost({
  attemptId,
  node,
  onScenarioActivity,
  storageKey,
  topologyNodes,
  title,
  url
}: {
  attemptId: string;
  node: SimulatorTopologyNode;
  onScenarioActivity?: () => void;
  storageKey: string;
  topologyNodes: SimulatorTopologyNode[];
  title: string;
  url: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const lastSnapshotRef = useRef<string | null>(null);
  const legacyReloadCountRef = useRef(0);
  const [legacyStateReady, setLegacyStateReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLegacyStateReady(false);
    legacyReloadCountRef.current = 0;

    try {
      const existingRaw = window.localStorage.getItem(storageKey);
      const existingState = existingRaw ? JSON.parse(existingRaw) as Record<string, unknown> : {};
      const fallbackState = normalizeLegacyMachineState(
        createLegacyMachineBootstrapState(existingState),
        { attemptId, node, topologyNodes }
      );
      window.localStorage.setItem(storageKey, JSON.stringify(fallbackState));
      lastSnapshotRef.current = JSON.stringify(fallbackState);
    } catch {
      lastSnapshotRef.current = null;
    }

    async function seedLegacyState() {
      try {
        const loadModule = new Function("path", "return import(path)") as (path: string) => Promise<Record<string, unknown>>;
        const [reducersModule, configModule] = await Promise.all([
          loadModule("/simulator-v1/js/state/reducers.js"),
          loadModule("/simulator-v1/config/windowsConfig.js")
        ]);
        if (cancelled) return;

        const buildInitialState = reducersModule.buildInitialState as ((config: unknown) => Record<string, unknown>) | undefined;
        const existingRaw = window.localStorage.getItem(storageKey);
        const existingState = existingRaw ? JSON.parse(existingRaw) as Record<string, unknown> : null;
        const baseState = existingState ?? buildInitialState?.(configModule.windowsConfig) ?? createLegacyMachineBootstrapState({});
        const nextState = normalizeLegacyMachineState(baseState, { attemptId, node, topologyNodes });

        window.localStorage.setItem(storageKey, JSON.stringify(nextState));
        lastSnapshotRef.current = JSON.stringify(nextState);
        setLegacyStateReady(true);
      } catch {
        lastSnapshotRef.current = null;
        setLegacyStateReady(true);
      }
    }

    void seedLegacyState();

    return () => {
      cancelled = true;
    };
  }, [attemptId, node, storageKey, topologyNodes]);

  useEffect(() => {
    try {
      window.localStorage.setItem(`${storageKey}:v2-context`, JSON.stringify({
        attemptId,
        mode: "scenario",
        nodeId: node.id,
        nodeType: node.nodeType,
        roles: node.roles
      }));
      lastSnapshotRef.current = window.localStorage.getItem(storageKey);
    } catch {
      lastSnapshotRef.current = null;
    }
  }, [attemptId, node.id, node.nodeType, node.roles, storageKey]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin || event.source !== iframeRef.current?.contentWindow) return;
      if (!event.data || typeof event.data !== "object") return;
      onScenarioActivity?.();
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onScenarioActivity]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      let currentSnapshot: string | null = null;
      try {
        currentSnapshot = window.localStorage.getItem(storageKey);
        if (currentSnapshot && node.nodeType !== "server") {
          const currentState = JSON.parse(currentSnapshot) as Record<string, unknown>;
          const normalizedState = normalizeLegacyMachineState(currentState, { attemptId, node, topologyNodes });
          const normalizedSnapshot = JSON.stringify(normalizedState);

          if (normalizedSnapshot !== currentSnapshot) {
            window.localStorage.setItem(storageKey, normalizedSnapshot);
            currentSnapshot = normalizedSnapshot;

            if (legacyReloadCountRef.current < 2) {
              legacyReloadCountRef.current += 1;
              iframeRef.current?.contentWindow?.location.replace(url);
            }
          }
        }
      } catch {
        currentSnapshot = null;
      }

      if (currentSnapshot !== lastSnapshotRef.current) {
        lastSnapshotRef.current = currentSnapshot;
        onScenarioActivity?.();
      }
    }, 2500);

    return () => window.clearInterval(interval);
  }, [attemptId, node, onScenarioActivity, storageKey, topologyNodes, url]);

  if (!legacyStateReady) {
    return createElement("div", {
      className: "legacy-simulator-frame legacy-simulator-frame--loading",
      "data-node-id": node.id,
      "data-simulator-runtime": "legacy-compat-loading"
    }, "Simulator wordt voorbereid...");
  }

  return createElement("iframe", {
    className: "legacy-simulator-frame",
    "data-attempt-id": attemptId,
    "data-node-id": node.id,
    "data-node-type": node.nodeType,
    "data-simulator-runtime": "legacy-compat",
    "data-storage-key": storageKey,
    onLoad: (event) => {
      enforceLegacyClientState(event.currentTarget, {
        attemptId,
        node,
        reloadCountRef: legacyReloadCountRef,
        storageKey,
        topologyNodes,
        url
      });
      installLegacyClientAppGuard(event.currentTarget, node);
    },
    ref: iframeRef,
    src: url,
    title
  });
}

function legacyMachineRole(node: SimulatorTopologyNode): string {
  if (node.nodeType === "server") {
    return node.roles.includes("domainController") ? "domain-controller" : "member-server";
  }
  return "workstation";
}

function legacyMachineEdition(node: SimulatorTopologyNode): "client" | "server" {
  return node.nodeType === "server" ? "server" : "client";
}

function createLegacyMachineBootstrapState(existingState: Record<string, unknown>): Record<string, unknown> {
  const existingSession = existingState.session && typeof existingState.session === "object"
    ? existingState.session as Record<string, unknown>
    : {};
  const existingUsers = Array.isArray(existingState.users) ? existingState.users : [];
  const users = existingUsers.length > 0
    ? existingUsers
    : [{
        id: "student",
        username: "Student",
        password: "Student1!",
        fullName: "Student",
        profile: { wallpaper: "default" }
      }];
  const currentUser = users[0] && typeof users[0] === "object" ? users[0] as Record<string, unknown> : null;

  return {
    ...existingState,
    users,
    groups: Array.isArray(existingState.groups) ? existingState.groups : [],
    session: {
      loggedIn: Boolean(existingSession.loggedIn),
      currentUserId: existingSession.currentUserId ?? currentUser?.id ?? null,
      currentUsername: existingSession.currentUsername ?? currentUser?.username ?? null,
      loginTime: existingSession.loginTime ?? null,
      lockScreen: existingSession.lockScreen ?? true,
      _loginError: null
    },
    filesystem: existingState.filesystem && typeof existingState.filesystem === "object" ? existingState.filesystem : { nodes: {} },
    recyclebin: existingState.recyclebin && typeof existingState.recyclebin === "object" ? existingState.recyclebin : { items: [] },
    network: existingState.network && typeof existingState.network === "object" ? existingState.network : {
      adapters: [{
        name: "Ethernet",
        ipv4: "192.168.10.20",
        subnet: "255.255.255.0",
        gateway: "192.168.10.1",
        dns: ["192.168.10.10"],
        dhcp: true
      }],
      drives: []
    },
    envVars: existingState.envVars && typeof existingState.envVars === "object" ? existingState.envVars : { system: {}, user: {} },
    server: existingState.server && typeof existingState.server === "object" ? existingState.server : {
      enabled: false,
      productName: "Windows 11 Enterprise",
      deployment: { postDeployRoleId: null, postDeployMessage: "", rebootRequired: false, rebootReason: "" },
      activeDirectory: { promoted: false, organizationalUnits: [], users: [], groups: [], computers: [], groupPolicies: [] },
      dns: { zones: [], forwarders: ["1.1.1.1", "8.8.8.8"] },
      dhcp: { scopes: [], leases: [] },
      roles: []
    },
    events: Array.isArray(existingState.events) ? existingState.events : [],
    auditLog: Array.isArray(existingState.auditLog) ? existingState.auditLog : []
  };
}

function enforceLegacyClientState(
  iframe: HTMLIFrameElement,
  input: {
    attemptId: string;
    node: SimulatorTopologyNode;
    reloadCountRef: { current: number };
    storageKey: string;
    topologyNodes: SimulatorTopologyNode[];
    url: string;
  }
): void {
  if (input.node.nodeType === "server") return;

  try {
    const raw = window.localStorage.getItem(input.storageKey);
    if (!raw) return;

    const state = JSON.parse(raw) as Record<string, unknown>;
    const normalizedState = normalizeLegacyMachineState(state, {
      attemptId: input.attemptId,
      node: input.node,
      topologyNodes: input.topologyNodes
    });
    const normalizedRaw = JSON.stringify(normalizedState);
    const shouldReload = normalizedRaw !== raw
      || iframe.contentDocument?.body?.textContent?.includes("Server Manager") === true;

    window.localStorage.setItem(input.storageKey, normalizedRaw);
    iframe.dataset.legacyClientState = "client";

    if (shouldReload && input.reloadCountRef.current < 2) {
      input.reloadCountRef.current += 1;
      iframe.contentWindow?.location.replace(input.url);
    }
  } catch {
    iframe.dataset.legacyClientState = "unavailable";
  }
}

function installLegacyClientAppGuard(iframe: HTMLIFrameElement, node: SimulatorTopologyNode): void {
  if (node.nodeType === "server") return;

  try {
    const doc = iframe.contentDocument;
    if (!doc) return;

    const blockedIds = new Set<string>(legacyV1ServerOnlyAppIds);
    const blockedLabels = new Set([
      "Server Manager",
      "Active Directory Users and Computers",
      "Group Policy Management",
      "DNS Manager",
      "DHCP Manager"
    ]);

    const isBlockedElement = (element: Element): boolean => {
      const appId = element.getAttribute("data-app");
      if (appId && blockedIds.has(appId)) return true;
      const label = element.textContent?.replace(/\s+/g, " ").trim();
      return Boolean(label && blockedLabels.has(label));
    };

    const pruneServerOnlyApps = () => {
      doc.querySelectorAll(".sm-app, .desktop-icon, [data-app]").forEach((element) => {
        if (isBlockedElement(element)) element.remove();
      });
    };

    doc.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const blocked = target?.closest(".sm-app, .desktop-icon, [data-app]");
      if (blocked && isBlockedElement(blocked)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    doc.addEventListener("dblclick", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const blocked = target?.closest(".sm-app, .desktop-icon, [data-app]");
      if (blocked && isBlockedElement(blocked)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    const observer = new MutationObserver(pruneServerOnlyApps);
    observer.observe(doc.body, { childList: true, subtree: true });
    iframe.dataset.legacyClientAppGuard = "installed";
    pruneServerOnlyApps();
  } catch {
    iframe.dataset.legacyClientAppGuard = "unavailable";
  }
}

function normalizeLegacyMachineState(
  state: Record<string, unknown>,
  input: { attemptId: string; node: SimulatorTopologyNode; topologyNodes: SimulatorTopologyNode[] }
): Record<string, unknown> {
  const isServer = input.node.nodeType === "server";
  const system = { ...(state.system && typeof state.system === "object" ? state.system as Record<string, unknown> : {}) };
  const server = { ...(state.server && typeof state.server === "object" ? state.server as Record<string, unknown> : {}) };
  const envVars = { ...(state.envVars && typeof state.envVars === "object" ? state.envVars as Record<string, unknown> : {}) };
  const systemEnv = { ...(envVars.system && typeof envVars.system === "object" ? envVars.system as Record<string, unknown> : {}) };
  const network = { ...(state.network && typeof state.network === "object" ? state.network as Record<string, unknown> : {}) };
  const adapters = Array.isArray(network.adapters) ? [...network.adapters] : [];
  const firstAdapter = adapters[0] && typeof adapters[0] === "object" ? { ...adapters[0] as Record<string, unknown> } : null;
  const role = legacyMachineRole(input.node);
  const edition = legacyMachineEdition(input.node);

  system.hostname = input.node.id;
  system.editionType = edition;
  system.edition = isServer ? "Windows Server 2025 Standard" : "Windows 11 Enterprise";
  systemEnv.COMPUTERNAME = input.node.id;
  envVars.system = systemEnv;
  server.enabled = isServer;
  server.productName = isServer ? "Windows Server 2025 Standard" : "Windows 11 Enterprise";

  if (firstAdapter) {
    firstAdapter.ipv4 = input.node.ip ?? (isServer ? "192.168.10.10" : "192.168.10.20");
    firstAdapter.subnet = "255.255.255.0";
    firstAdapter.gateway = "192.168.10.1";
    firstAdapter.dhcp = !isServer;
    firstAdapter.dns = isServer ? ["127.0.0.1", "192.168.10.10"] : ["192.168.10.10"];
    adapters[0] = firstAdapter;
    network.adapters = adapters;
  }

  return {
    ...state,
    system,
    server,
    envVars,
    network,
    scenarioContext: {
      instanceId: input.attemptId,
      domain: { name: defaultSimulatorTopology.sharedServices.directory?.domain ?? "advanced-ti.local" },
      machines: input.topologyNodes.map((topologyNode) => ({
        key: topologyNode.id,
        hostname: topologyNode.id,
        role: legacyMachineRole(topologyNode),
        edition: legacyMachineEdition(topologyNode),
        network: {
          ipv4: topologyNode.ip,
          subnet: "255.255.255.0",
          gateway: "192.168.10.1",
          dns: topologyNode.nodeType === "server" ? ["127.0.0.1", "192.168.10.10"] : ["192.168.10.10"],
          dhcp: topologyNode.nodeType !== "server"
        }
      }))
    },
    __firstdesk: {
      ...(state.__firstdesk && typeof state.__firstdesk === "object" ? state.__firstdesk as Record<string, unknown> : {}),
      instanceId: input.attemptId,
      machineKey: input.node.id,
      machineRole: role,
      machineEdition: edition,
      lastLoadedAt: new Date().toISOString()
    }
  };
}

function ScenarioShellView({
  onBack,
  simulatorApps,
  ticket
}: {
  onBack: () => void;
  simulatorApps: StudentSimulatorAppView[];
  ticket: StudentTicketWorkspaceItem;
}) {
  const [reportOpen, setReportOpen] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [activeMachine, setActiveMachine] = useState("CLIENT01");
  const [escalationTarget, setEscalationTarget] = useState<{ id: string; title: string } | null>(null);
  const [escalationReason, setEscalationReason] = useState("");
  const [report, setReport] = useState<ReportFormState>(() => emptyReportForm(ticket));
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState("");
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeQuery, setKnowledgeQuery] = useState("");
  const [knowledgeType, setKnowledgeType] = useState<KnowledgeArticle["type"] | "all">("all");
  const [knowledgeCategory, setKnowledgeCategory] = useState("all");
  const [knowledgeApp, setKnowledgeApp] = useState("all");
  const [activeKnowledgeId, setActiveKnowledgeId] = useState<string | null>(null);
  const [legacyKnowledgeArticles, setLegacyKnowledgeArticles] = useState<KnowledgeArticle[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [targetSummary, setTargetSummary] = useState(ticket.targetSummary);
  const [escalatedTargetIds, setEscalatedTargetIds] = useState<Set<string>>(() => new Set());
  const [resetNonce, setResetNonce] = useState(0);
  const [simulatorRuntimeMode] = useState<SimulatorRuntimeMode>(resolveSimulatorRuntimeMode);
  const autoCheckTimerRef = useRef<number | null>(null);
  const ticketTopology = useMemo(() => simulatorTopologyForTicket({ environment: ticket.environment }), [ticket.environment]);
  const total = targetSummary.totalTargets;
  const visibleTargets = targetSummary.visibleTargets.map((target) => (
    escalatedTargetIds.has(target.id) && target.status !== "passed"
      ? { ...target, status: "escalated" as TargetResultStatus }
      : target
  ));
  const completed = visibleTargets.filter((target) => target.status === "passed").length;
  const reportReadyCount = visibleTargets.filter((target) => (
    target.status === "passed" || target.status === "escalated" || target.status === "manual_review"
  )).length;
  const reportUnlocked = total > 0 && targetSummary.hiddenRemainingCount === 0 && reportReadyCount >= total;
  const simulatorAttemptId = resolveSimulatorBridgeAttemptId(ticket.id);
  const nativeAttemptId = simulatorAttemptId ?? ticket.id;
  const machines = ticketTopology.nodes.map((node) => ({
    key: node.id,
    node,
    role: node.roles.join(", "),
    ip: node.ip ?? "-"
  }));
  const activeNode = nodeForSimulatorId(activeMachine, ticketTopology);
  const nativeMachine = activeNode.nodeType === "server" ? "SRV01" : "CLIENT01";
  const simulatorStorageKey = buildSimulatorStorageKey({
    attemptId: nativeAttemptId,
    nodeId: activeNode.id
  });
  const legacySimulatorUrl = buildScenarioSimulatorUrl({
    attemptId: nativeAttemptId,
    machine: activeNode.legacyMachineKey,
    nodeId: activeNode.id,
    nodeType: activeNode.nodeType,
    simulatorStorageKey,
    ticketId: ticket.id
  });

  useEffect(() => {
    setReport(emptyReportForm(ticket));
    setSavedMessage("");
    setTargetSummary(ticket.targetSummary);
    setEscalatedTargetIds(new Set());
  }, [ticket]);

  useEffect(() => {
    if (!ticketTopology.nodes.some((node) => node.id === activeMachine)) {
      setActiveMachine(ticketTopology.nodes[0]?.id ?? "CLIENT01");
    }
  }, [activeMachine, ticketTopology]);

  useEffect(() => () => {
    if (autoCheckTimerRef.current) {
      window.clearTimeout(autoCheckTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!knowledgeOpen || legacyKnowledgeArticles.length || knowledgeLoading) return;
    setKnowledgeLoading(true);
    fetch("/api/v2/knowledgebase", { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) throw new Error(`knowledgebase ${response.status}`);
        return response.json() as Promise<LegacyKnowledgebasePayload>;
      })
      .then((body) => {
        const articles = buildKnowledgeItems(body);
        if (articles.length) {
          setLegacyKnowledgeArticles(articles);
          setActiveKnowledgeId((current) => current ?? articles[0]?.id ?? null);
        }
      })
      .catch(() => {
        setSavedMessage("Kennisbank kon niet volledig worden geladen; lokale basisartikelen blijven beschikbaar.");
      })
      .finally(() => setKnowledgeLoading(false));
  }, [knowledgeLoading, knowledgeOpen, legacyKnowledgeArticles.length]);

  function updateReport(field: keyof ReportFormState, value: string) {
    setReport((current) => ({ ...current, [field]: value }));
  }

  function resetActiveMachineState() {
    try {
      window.localStorage.removeItem(simulatorStorageKey);
      window.localStorage.removeItem(`${simulatorStorageKey}:v2-context`);
    } catch {
      // ignore local storage errors in restricted contexts
    }
    setResetNonce((current) => current + 1);
    setSavedMessage(`${activeMachine} is gereset naar de startsituatie.`);
  }

  async function runStudentSelfCheck(options: { automatic?: boolean; silent?: boolean } = {}) {
    if (!simulatorAttemptId) {
      setSavedMessage("Self-check is niet beschikbaar voor deze ticketinstantie.");
      return;
    }
    try {
      const evaluateResponse = await fetch(
        `/api/v2/assessment/attempts/${encodeURIComponent(simulatorAttemptId)}/self-check`,
        {
          credentials: "include",
          method: "POST"
        }
      );
      if (!evaluateResponse.ok) {
        throw new Error(`self-check failed: ${evaluateResponse.status}`);
      }

      const targetsResponse = await fetch(
        `/api/v2/assessment/attempts/${encodeURIComponent(simulatorAttemptId)}/student-targets`,
        {
          credentials: "include"
        }
      );
      if (!targetsResponse.ok) {
        throw new Error(`student-targets failed: ${targetsResponse.status}`);
      }
      const view = await targetsResponse.json();
      if (view?.targetSummary) {
        setTargetSummary(view.targetSummary);
      }
      if (!options.silent) {
        setSavedMessage(options.automatic
          ? "Voortgang is automatisch bijgewerkt."
          : "Targets en voortgang zijn bijgewerkt.");
      }
    } catch {
      if (!options.silent) {
        setSavedMessage(options.automatic
          ? "Automatische voortgangscontrole kon niet worden uitgevoerd."
          : "Voortgang kon niet worden bijgewerkt. Probeer het opnieuw.");
      }
    }
  }

  function scheduleAutomaticSelfCheck() {
    if (!simulatorAttemptId) return;
    if (autoCheckTimerRef.current) {
      window.clearTimeout(autoCheckTimerRef.current);
    }
    setSavedMessage("Wijziging vastgelegd. Targets worden automatisch gecontroleerd...");
    autoCheckTimerRef.current = window.setTimeout(() => {
      autoCheckTimerRef.current = null;
      void runStudentSelfCheck({ automatic: true });
    }, 650);
  }

  useEffect(() => {
    if (!simulatorAttemptId) return;
    void runStudentSelfCheck({ automatic: true, silent: true });
  }, [simulatorAttemptId]);

  const fallbackKnowledgeArticles = buildKnowledgeItems({ artikelen: [
    {
      id: "kb-domain",
      type: "actie",
      categorie: "Netwerk & domein",
      categorieId: "netwerk-domein",
      applicatie: [{ id: "netSettings", naam: "Network & Internet Settings" }, { id: "dns-manager", naam: "DNS Manager" }],
      titel: "Domein- en DNS-baseline",
      korteBeschrijving: "Gebruik deze route als domain join, DNS of loginvalidatie niet klopt.",
      stapsgewijzeHandelingen: [
        "Controleer actieve machine (CLIENT01 vs SRV01) en netwerkadapter.",
        "Valideer DNS-clientinstellingen voordat je domain join uitvoert.",
        "Controleer dat SRV01 de juiste zone/records bevat en dat CLIENT01 de interne DNS gebruikt.",
        "Voer na wijziging een self-check uit om targetstatus direct te zien."
      ]
    },
    {
      id: "kb-services",
      type: "actie",
      categorie: "Security & beheer",
      categorieId: "security-beheer",
      applicatie: [{ id: "services", naam: "Services" }, { id: "wf", naam: "Windows Defender Firewall" }],
      titel: "Services, firewall en RDP",
      korteBeschrijving: "Stappen voor service-startup, firewallprofielen en remote desktop toegang.",
      stapsgewijzeHandelingen: [
        "Zet service status en startup-type expliciet volgens opdracht.",
        "Controleer firewall-regelrichting en profielkoppeling.",
        "Bevestig RDP + toegestane groep en draai daarna self-check.",
        "Escaleren is alleen nodig als de simulatoractie faalt of de opdracht onduidelijk is."
      ]
    },
    {
      id: "kb-files",
      type: "actie",
      categorie: "Bestanden & rechten",
      categorieId: "bestanden-rechten",
      applicatie: [{ id: "explorer", naam: "File Explorer" }],
      titel: "Files, shares en rechten",
      korteBeschrijving: "Mapstructuur, share-rechten en NTFS ACL in de juiste volgorde.",
      stapsgewijzeHandelingen: [
        "Maak eerst mapstructuur aan en pas daarna share/ACL aan.",
        "Gebruik consistente principal-namen bij permissies.",
        "Controleer sharing en security tabs afzonderlijk.",
        "Evalueer voortgang met self-check voor je verslag indient."
      ]
    },
    {
      id: "kb-report",
      type: "actie",
      categorie: "Afronden",
      categorieId: "afronden",
      titel: "Servicedeskverslag afronden",
      korteBeschrijving: "Wanneer alle targets groen zijn of gemotiveerd geescaleerd, vul je het verslag in.",
      stapsgewijzeHandelingen: [
        "Beschrijf eerst de analyse: wat was de oorzaak of waarschijnlijke oorzaak?",
        "Noteer daarna de uitgevoerde acties per machine.",
        "Leg vast hoe je hebt gevalideerd dat de gebruiker weer verder kan.",
        "Lever pas in wanneer de verslagknop actief is."
      ]
    }
  ] });
  const knowledgeArticles = legacyKnowledgeArticles.length ? legacyKnowledgeArticles : fallbackKnowledgeArticles;
  const knowledgeCategories = [
    { id: "all", label: "Alle categorieen" },
    ...Array.from(new Map(knowledgeArticles.map((article) => [article.categoryId, article.category])).entries())
      .filter(([id]) => id && id !== "all")
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label, "nl"))
  ];
  const knowledgeApps = [
    { id: "all", label: "Alle apps" },
    ...Array.from(new Map(knowledgeArticles.flatMap((article) => (
      article.appIds.map((appId, index) => [appId, article.appNames[index] ?? appId] as const)
    ))).entries())
      .filter(([id]) => id && id !== "all")
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label, "nl"))
  ];
  const filteredKnowledgeArticles = knowledgeArticles.filter((article) => {
    const query = knowledgeQuery.trim().toLowerCase();
    const matchesType = knowledgeType === "all" || article.type === knowledgeType;
    const matchesCategory = knowledgeCategory === "all" || article.categoryId === knowledgeCategory;
    const matchesApp = knowledgeApp === "all" || article.appIds.includes(knowledgeApp);
    const matchesQuery = !query || query.split(/\s+/).filter(Boolean).every((token) => article.searchCorpus.includes(token));
    return matchesType && matchesCategory && matchesApp && matchesQuery;
  });
  const activeKnowledgeArticle = filteredKnowledgeArticles.find((article) => article.id === activeKnowledgeId)
    ?? filteredKnowledgeArticles[0]
    ?? null;

  function saveEscalation() {
    if (escalationReason.trim().length < 10) return;
    if (escalationTarget) {
      setEscalatedTargetIds((current) => {
        const next = new Set(current);
        next.add(escalationTarget.id);
        return next;
      });
    }
    setSavedMessage(`Escalatie opgeslagen voor ${escalationTarget?.title ?? "target"}.`);
    setEscalationTarget(null);
    setEscalationReason("");
  }

  function openReport() {
    if (!reportUnlocked) {
      setSavedMessage("Rond eerst alle targets af met self-check of escaleer open targets met reden.");
      return;
    }
    setReportOpen(true);
  }

  async function submitReport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reportUnlocked) {
      setSavedMessage("Verslag indienen kan pas na afgeronde of geescaleerde targets.");
      return;
    }
    setSaving(true);
    setSavedMessage("");

    try {
      const response = await fetch(`/api/v2/game/attempts/${ticket.id}/submit`, {
        credentials: "include",
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          diagnosisText: `${report.summary}\n\nAnalyse:\n${report.analysis}`,
          resolutionText: `${report.actions}\n\nMachines:\n${report.machines}\n\nSystemen:\n${report.systems}`,
          reflectionText: `Type: ${report.type}\nStatus: ${report.ticketStatus}\nValidatie:\n${report.validation}`
        })
      });

      if (!response.ok) {
        throw new Error(`report save failed: ${response.status}`);
      }

      setSavedMessage("Verslag opgeslagen en ingeleverd.");
      setReportOpen(false);
    } catch {
      setSavedMessage("Opslaan is niet gelukt. Probeer het opnieuw.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="v2-scenario-fullscreen" aria-label="Scenario werkplek">
      <header className="sc-topbar">
        <button className="sc-topbar-brand" type="button" onClick={onBack}>FirstDesk</button>
        <span className="sc-topbar-id">Scenario #{ticket.number}</span>
        <div className="sc-topbar-feedback">
          <span>Escalatiemodus (feedbackmodus)</span>
          <button
            aria-checked={feedbackMode}
            className="sc-switch"
            type="button"
            onClick={() => setFeedbackMode((current) => !current)}
          />
        </div>
        <button className="sc-topbar-back" type="button" onClick={onBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          Dashboard
        </button>
      </header>

      <div className="sc-layout">
        <aside className="sc-panel" id="scenario-left-panel">
          <div className="sc-panel-section" id="scenario-info-section">
            <p className="sc-kicker">Scenario-informatie</p>
            <h2>Wat is de hulpvraag?</h2>
            <p className="sc-ticket-title">{ticket.title}</p>
            <p className="sc-ticket-desc">{ticket.description}</p>
            <table className="sc-meta-table">
              <tbody>
                <tr><td>Werkmodus</td><td>{ticket.type}</td></tr>
                <tr><td>Domein</td><td>{ticket.environment}</td></tr>
                <tr><td>Instance</td><td>#{ticket.id}</td></tr>
                <tr><td>Targets</td><td>{total}</td></tr>
                <tr><td>Apps</td><td>{simulatorApps.length} beschikbaar</td></tr>
              </tbody>
            </table>

            <section className="sc-targets" id="target-overlay" aria-live="polite">
              <div className="sc-target-header">
                <span className="sc-target-count">{completed} / {total} targets gereed</span>
                <button className="sc-target-refresh-btn" type="button" onClick={() => void runStudentSelfCheck()}>
                  Targets opnieuw controleren
                </button>
              </div>
              {visibleTargets.map((target) => (
                <div className="sc-target-row fd-target-row" data-task-id={target.id} key={target.id}>
                  <div>
                    <strong>{target.title}</strong>
                    <p>{target.description || "Controleer deze stap in de simulator."}</p>
                  </div>
                  <div className="sc-target-actions">
                    <span className={`sc-target-state fd-target-state ${targetStatusClass(target.status)}`}>{targetStatusLabel(target.status)}</span>
                    {feedbackMode ? (
                      <button
                        className="sc-target-escalate-btn fd-target-escalate-btn"
                        type="button"
                        onClick={() => setEscalationTarget({ id: target.id, title: target.title })}
                      >
                        Escaleer
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {targetSummary.hiddenRemainingCount > 0 ? (
                <div className="sc-target-row fd-target-row">
                  <div>
                    <strong>{targetSummary.hiddenRemainingCount} verborgen targets</strong>
                    <p>Deze targets blijven server-side afgeschermd totdat ze vrijgegeven mogen worden.</p>
                  </div>
                  <div className="sc-target-actions">
                    <span className="sc-target-state fd-target-state">Verborgen</span>
                  </div>
                </div>
              ) : null}
            </section>
          </div>

          <div className="sc-panel-section">
            <p className="sc-kicker">Machineoverzicht</p>
            <h2>Scenario-infrastructuur</h2>
            {machines.map((machine) => (
              <button
                className={`sc-machine-row ${activeMachine === machine.key ? "sc-machine-row--active" : ""}`}
                key={machine.key}
                type="button"
                onClick={() => setActiveMachine(machine.key)}
              >
                <div className="sc-machine-left">
                  <span className="material-symbols-outlined">computer</span>
                  <div>
                    <div className="sc-machine-name">{machine.key}</div>
                    <div className="sc-machine-sub">{machine.role} - {machine.ip}</div>
                  </div>
                </div>
                <span className="sc-badge sc-badge--running">Running</span>
              </button>
            ))}
          </div>

          <div className="sc-panel-footer is-attention" id="report-cta">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <button className="sc-report-btn" type="button" onClick={() => setKnowledgeOpen(true)}>
                <Info size={16} />
                Kennisbank
              </button>
            </div>
            <button className="sc-report-btn is-attention" disabled={!reportUnlocked} type="button" onClick={openReport}>
              <span className="material-symbols-outlined">description</span>
              Servicedeskverslag openen
            </button>
            <p className="sc-report-callout">
              <span className="material-symbols-outlined">task_alt</span>
              {reportUnlocked ? "Alle targets zijn klaar voor het verslag" : "Rond targets af of escaleer ze voordat je het verslag opent"}
            </p>
            {savedMessage ? <p className="sc-report-callout">{savedMessage}</p> : null}
          </div>
        </aside>

        <div className="sc-sim">
          <div className="sc-machine-tabs">
            <div className="sc-machine-tabs-list">
              {machines.map((machine) => (
                <button
                  className={activeMachine === machine.key ? "sc-tab fd-button--primary" : "sc-tab"}
                  key={machine.key}
                  type="button"
                  onClick={() => setActiveMachine(machine.key)}
                >
                  <span className="material-symbols-outlined">computer</span>
                  {machine.key}
                </button>
              ))}
            </div>
            <button className="sc-tab sc-tab--reset" type="button">
              <span className="material-symbols-outlined">apps</span>
              {simulatorApps.length} apps
            </button>
            <button className="sc-tab sc-tab--reset" type="button" onClick={() => setKnowledgeOpen(true)}>
              <Info size={16} />
              Kennisbank
            </button>
            <button className="sc-tab sc-tab--reset" type="button" onClick={resetActiveMachineState}>
              <span className="material-symbols-outlined">restart_alt</span>
              Reset machine
            </button>
          </div>
          <div className="sc-frame-area">
            {simulatorRuntimeMode === "legacy" ? (
              <LegacyV1SimulatorHost
                attemptId={nativeAttemptId}
                key={`${ticket.id}:${activeMachine}:${resetNonce}:legacy`}
                node={activeNode}
                onScenarioActivity={scheduleAutomaticSelfCheck}
                storageKey={simulatorStorageKey}
                topologyNodes={ticketTopology.nodes}
                title={`${ticket.title} legacy simulator`}
                url={legacySimulatorUrl}
              />
            ) : simulatorRuntimeMode === "compare" ? (
              <div className="simulator-compare-grid" data-simulator-runtime="compare">
                <NativeSimulatorDesktop
                  activeMachine={nativeMachine}
                  attemptId={nativeAttemptId}
                  initialShellPhase="locked"
                  key={`${ticket.id}:${activeMachine}:${resetNonce}:native`}
                  onCommandComplete={(result) => {
                    if (result.ok) scheduleAutomaticSelfCheck();
                  }}
                  onCommandMessage={setSavedMessage}
                  simulatorApps={simulatorApps}
                  ticketId={ticket.id}
                />
                <LegacyV1SimulatorHost
                  attemptId={nativeAttemptId}
                  key={`${ticket.id}:${activeMachine}:${resetNonce}:legacy`}
                  node={activeNode}
                  onScenarioActivity={scheduleAutomaticSelfCheck}
                  storageKey={simulatorStorageKey}
                  topologyNodes={ticketTopology.nodes}
                  title={`${ticket.title} legacy simulator`}
                  url={legacySimulatorUrl}
                />
              </div>
            ) : simulatorRuntimeMode === "fd-native" ? (
              <FirstDeskSimulatorShell
                activeMachine={nativeMachine}
                attemptId={nativeAttemptId}
                initialShellPhase="locked"
                key={`${ticket.id}:${activeMachine}:${resetNonce}:fd-native`}
                onCommandComplete={(result) => {
                  if (result.ok) scheduleAutomaticSelfCheck();
                }}
                onCommandMessage={setSavedMessage}
                simulatorApps={simulatorApps}
                ticketId={ticket.id}
              />
            ) : (
              <NativeSimulatorDesktop
                activeMachine={nativeMachine}
                attemptId={nativeAttemptId}
                initialShellPhase="locked"
                key={`${ticket.id}:${activeMachine}:${resetNonce}`}
                onCommandComplete={(result) => {
                  if (result.ok) scheduleAutomaticSelfCheck();
                }}
                onCommandMessage={setSavedMessage}
                simulatorApps={simulatorApps}
                ticketId={ticket.id}
              />
            )}
          </div>
        </div>
      </div>

      {escalationTarget ? (
        <div className="sc-escalation-backdrop" role="presentation">
          <section className="sc-escalation-modal" role="dialog" aria-modal="true" aria-labelledby="escalation-modal-title">
            <div className="sc-escalation-head">
              <div>
                <p className="sc-kicker">Target escaleren</p>
                <h3 id="escalation-modal-title">Probleem melden bij target</h3>
                <p>{escalationTarget.title}</p>
              </div>
              <button className="sc-escalation-close" type="button" aria-label="Sluiten" onClick={() => setEscalationTarget(null)}>
                <X aria-hidden="true" size={18} />
              </button>
            </div>
            <div className="sc-escalation-field">
              <span>Toelichting</span>
              <textarea
                maxLength={1000}
                placeholder="Beschrijf concreet wat niet werkt, welke stappen je hebt geprobeerd en waarom deze target niet haalbaar is."
                value={escalationReason}
                onChange={(event) => setEscalationReason(event.target.value)}
              />
              <p className="sc-escalation-hint">Minimaal 10 tekens. Je docent ziet deze toelichting terug bij je resultaat.</p>
            </div>
            <div className="sc-escalation-actions">
              <button className="sc-escalation-cancel" type="button" onClick={() => setEscalationTarget(null)}>Annuleren</button>
              <button className="sc-escalation-save" disabled={escalationReason.trim().length < 10} type="button" onClick={saveEscalation}>
                Escalatie opslaan
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {reportOpen ? (
        <div className="fd-modal-backdrop" role="presentation">
          <section className="fd-modal" role="dialog" aria-modal="true" aria-labelledby="report-modal-title">
            <header className="fd-modal-header">
              <div>
                <p className="sc-kicker">Servicedeskverslag</p>
                <h2 id="report-modal-title">Afronding melding</h2>
              </div>
              <button className="fd-modal-close" type="button" aria-label="Sluiten" onClick={() => setReportOpen(false)}>
                <X aria-hidden="true" size={18} />
              </button>
            </header>
            <form className="fd-modal-form" onSubmit={submitReport}>
              <div className="fd-modal-grid">
                <label>
                  Samenvatting
                  <input value={report.summary} onChange={(event) => updateReport("summary", event.target.value)} />
                </label>
                <label>
                  Type
                  <select value={report.type} onChange={(event) => updateReport("type", event.target.value)}>
                    <option>Servicedesk</option>
                    <option>Game</option>
                  </select>
                </label>
                <label>
                  Ticketstatus
                  <select value={report.ticketStatus} onChange={(event) => updateReport("ticketStatus", event.target.value)}>
                    <option value="opgelost">Opgelost</option>
                    <option value="wacht_op_review">Wacht op review</option>
                    <option value="doorgezet">Doorgezet</option>
                  </select>
                </label>
                <label>
                  Gewijzigde machines
                  <input value={report.machines} onChange={(event) => updateReport("machines", event.target.value)} />
                </label>
                <label className="fd-modal-field-wide">
                  Analyse
                  <textarea required minLength={10} value={report.analysis} onChange={(event) => updateReport("analysis", event.target.value)} />
                </label>
                <label className="fd-modal-field-wide">
                  Uitgevoerde acties
                  <textarea required minLength={10} value={report.actions} onChange={(event) => updateReport("actions", event.target.value)} />
                </label>
                <label>
                  Systemen
                  <input value={report.systems} onChange={(event) => updateReport("systems", event.target.value)} />
                </label>
                <label>
                  Validatie
                  <input required value={report.validation} onChange={(event) => updateReport("validation", event.target.value)} />
                </label>
              </div>
              <div className="fd-modal-actions">
                <button className="sc-btn-primary" disabled={saving} type="submit">
                  <span className="material-symbols-outlined">save</span>
                  {saving ? "Opslaan..." : "Verslag opslaan"}
                </button>
                <button className="fd-modal-close" disabled={saving} type="button" onClick={() => setReportOpen(false)}>Sluiten</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {knowledgeOpen ? (
        <aside className="fd-kb-panel" role="dialog" aria-modal="true" aria-labelledby="knowledge-modal-title">
          <header className="fd-kb-head">
            <div>
              <p className="fd-kb-kicker">FirstDesk Handleiding</p>
              <h2 id="knowledge-modal-title">FirstDesk kennisbank</h2>
            </div>
            <button className="fd-kb-close" type="button" aria-label="Sluiten" onClick={() => setKnowledgeOpen(false)}>
              Sluiten
            </button>
          </header>

          <section className="fd-kb-controls">
            <label className="fd-kb-control">
              <span>Zoek</span>
              <input
                autoFocus
                placeholder="Zoek op actie, app, run-commando..."
                type="search"
                value={knowledgeQuery}
                onChange={(event) => setKnowledgeQuery(event.target.value)}
              />
            </label>
            <label className="fd-kb-control">
              <span>Type</span>
              <select value={knowledgeType} onChange={(event) => setKnowledgeType(event.target.value as KnowledgeArticle["type"] | "all")}>
                <option value="all">Alles</option>
                <option value="actie">Acties</option>
                <option value="app">Apps</option>
              </select>
            </label>
            <label className="fd-kb-control">
              <span>Categorie</span>
              <select value={knowledgeCategory} onChange={(event) => setKnowledgeCategory(event.target.value)}>
                {knowledgeCategories.map((category) => (
                  <option key={category.id} value={category.id}>{category.label}</option>
                ))}
              </select>
            </label>
            <label className="fd-kb-control">
              <span>App-filter</span>
              <select value={knowledgeApp} onChange={(event) => setKnowledgeApp(event.target.value)}>
                {knowledgeApps.map((app) => (
                  <option key={app.id} value={app.id}>{app.label}</option>
                ))}
              </select>
            </label>
          </section>

          <section className="fd-kb-body">
            <div className="fd-kb-results-wrap">
              <div className="fd-kb-results-meta">
                {knowledgeLoading
                  ? "Kennisbank laden..."
                  : `${filteredKnowledgeArticles.length} van ${knowledgeArticles.length} items`}
              </div>
              <ul className="fd-kb-results">
                {filteredKnowledgeArticles.map((article) => (
                  <li key={article.id}>
                    <button
                      className={activeKnowledgeArticle?.id === article.id ? "fd-kb-item is-active" : "fd-kb-item"}
                      type="button"
                      onClick={() => setActiveKnowledgeId(article.id)}
                    >
                      <span className="fd-kb-item-title">{article.title}</span>
                      <span className="fd-kb-item-meta">{article.category} · {article.appNames.join(", ") || "Algemeen"}</span>
                      <span className="fd-kb-item-badges">
                        <span className={article.type === "actie" ? "fd-kb-badge type-action" : "fd-kb-badge type-app"}>
                          {article.type === "actie" ? "Actie" : "App"}
                        </span>
                        {article.tickets.slice(0, 2).map((ticketRef) => (
                          <span className="fd-kb-badge context" key={ticketRef}>{ticketRef}</span>
                        ))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <article className="fd-kb-detail">
              {activeKnowledgeArticle ? (
                <>
                  <p className="fd-kb-detail-kicker">{activeKnowledgeArticle.category}</p>
                  <h3>{activeKnowledgeArticle.title}</h3>
                  <p>{activeKnowledgeArticle.summary}</p>
                  <div className="fd-kb-detail-meta">
                    <span>Van toepassing op: {activeKnowledgeArticle.appliesTo.join(", ")}</span>
                    {activeKnowledgeArticle.runCommands.length ? <span>Run: {activeKnowledgeArticle.runCommands.join(", ")}</span> : null}
                    {activeKnowledgeArticle.startPaths.length ? <span>Startpad: {activeKnowledgeArticle.startPaths.join(" / ")}</span> : null}
                  </div>
                  <ol>
                    {activeKnowledgeArticle.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  {activeKnowledgeArticle.keywords.length || activeKnowledgeArticle.tags.length ? (
                    <div className="fd-kb-tags">
                      {[...activeKnowledgeArticle.keywords, ...activeKnowledgeArticle.tags].slice(0, 10).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <h3>Geen item gevonden</h3>
                  <p>Pas de zoekterm of filters aan.</p>
                </>
              )}
            </article>
          </section>
        </aside>
      ) : null}
    </section>
  );
}

export function StudentWorkspace({ onRefresh, snapshot }: { onRefresh?: () => void; snapshot: StudentWorkspaceSnapshot }) {
  const tickets = snapshot.tickets;
  const [route, setRoute] = useState<StudentRoute>({ kind: "dashboard" });
  const selectedTicket = route.kind === "dashboard"
    ? null
    : tickets.find((ticket) => ticket.id === route.ticketId) ?? tickets[0] ?? null;

  useEffect(() => {
    if (route.kind !== "dashboard" && !tickets.some((ticket) => ticket.id === route.ticketId)) {
      setRoute({ kind: "dashboard" });
    }
  }, [route, tickets]);

  if (route.kind === "scenario" && selectedTicket) {
    return (
      <ScenarioShellView
        simulatorApps={snapshot.simulatorApps}
        ticket={selectedTicket}
        onBack={() => setRoute({ kind: "ticket", ticketId: selectedTicket.id })}
      />
    );
  }

  if (route.kind === "ticket" && selectedTicket) {
    return (
      <TicketDetailView
        ticket={selectedTicket}
        onBack={() => setRoute({ kind: "dashboard" })}
        onOpenScenario={() => setRoute({ kind: "scenario", ticketId: selectedTicket.id })}
      />
    );
  }

  return (
    <StudentDashboard
      snapshot={snapshot}
      onRefresh={onRefresh}
      onOpenTicket={(ticketId) => setRoute({ kind: "ticket", ticketId })}
    />
  );
}
